import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Inbox, RefreshCw, Mail, MessageSquare, Phone, Copy, UserPlus, X, ExternalLink, Send } from "lucide-react";

/**
 * Phase 68 — Unmatched Inbox
 *
 * Displays inbound Twilio SMS/WhatsApp + Resend email messages that couldn't
 * be matched to any existing candidate. Recruiters can:
 *   • Convert a row into a candidate (auto-de-dupe by email/phone)
 *   • Send a portal-claim invite (email only)
 *   • Dismiss a row that isn't relevant
 *
 * Webhook URLs (per-company) are surfaced at the top so admins can paste
 * them into the Twilio + Resend consoles without leaving the app.
 */
const CHANNEL_META = {
    sms:      { icon: MessageSquare, label: "SMS",      tint: "border-sky-200 bg-sky-50 text-sky-700" },
    whatsapp: { icon: Phone,         label: "WhatsApp", tint: "border-emerald-200 bg-emerald-50 text-emerald-700" },
    email:    { icon: Mail,          label: "Email",    tint: "border-violet-200 bg-violet-50 text-violet-700" },
};

export default function InboxUnmatched() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState("open");
    const [webhook, setWebhook] = useState(null);
    const [convertRow, setConvertRow] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/inbox/unmatched", { params: { status } });
            setRows(data?.items || []);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
    }, [status]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        api.get("/inbox/webhook-urls").then(({ data }) => setWebhook(data)).catch(() => {});
    }, []);

    const dismiss = async (row) => {
        setRows((r) => r.filter((x) => x.id !== row.id));
        try { await api.post(`/inbox/${row.id}/dismiss`); toast.success("Dismissed"); }
        catch (err) { toast.error(formatApiError(err)); load(); }
    };

    return (
        <div className="max-w-6xl mx-auto p-3 sm:p-6 lg:p-8" data-testid="inbox-page">
            <div className="mb-5">
                <div className="text-xs uppercase tracking-widest font-mono text-ikb">Unified inbox</div>
                <h1 className="font-display font-black text-2xl sm:text-4xl mt-1">Unmatched messages</h1>
                <p className="text-muted-foreground text-sm mt-2 max-w-2xl">
                    Inbound SMS, WhatsApp and email from senders that don&apos;t match any candidate yet.
                    Convert a message into a candidate, or send a portal-claim invite in one click.
                </p>
            </div>

            <WebhookConfigCard webhook={webhook} />

            <div className="border border-border bg-white">
                <div className="flex items-center justify-between gap-2 px-3 sm:px-4 py-2.5 border-b border-border">
                    <div className="inline-flex border border-border" data-testid="inbox-status-toggle">
                        {["open", "converted", "dismissed"].map((s) => (
                            <button
                                key={s}
                                type="button"
                                onClick={() => setStatus(s)}
                                className={`px-2.5 py-1 text-[11px] font-mono uppercase tracking-widest ${status === s ? "bg-ikb text-white" : "bg-white hover:bg-secondary"}`}
                                data-testid={`inbox-status-${s}`}
                            >
                                {s}
                            </button>
                        ))}
                    </div>
                    <Button variant="outline" className="rounded-none h-8" onClick={load} disabled={loading} data-testid="inbox-refresh">
                        <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
                    </Button>
                </div>

                {loading ? (
                    <div className="p-8 text-sm text-muted-foreground text-center">Loading…</div>
                ) : rows.length === 0 ? (
                    <div className="p-12 text-center">
                        <Inbox className="w-8 h-8 mx-auto text-muted-foreground/50" />
                        <div className="text-sm font-mono uppercase tracking-widest text-muted-foreground mt-3">No {status} messages</div>
                        <p className="text-sm text-muted-foreground mt-2">
                            Inbound SMS / WhatsApp / email that don&apos;t match a candidate will appear here.
                        </p>
                    </div>
                ) : (
                    <ul className="divide-y divide-border" data-testid="inbox-list">
                        {rows.map((row) => (
                            <InboxRow
                                key={row.id}
                                row={row}
                                onConvert={() => setConvertRow(row)}
                                onDismiss={() => dismiss(row)}
                                onInvite={async (email) => {
                                    try {
                                        const { data } = await api.post(`/inbox/${row.id}/invite-portal`, { email });
                                        toast.success(data?.invite_url ? "Portal invite issued — link copied" : "Invite issued");
                                        if (data?.invite_url) { try { await navigator.clipboard.writeText(data.invite_url); } catch { /* clipboard blocked */ } }
                                        load();
                                    } catch (err) { toast.error(formatApiError(err)); }
                                }}
                            />
                        ))}
                    </ul>
                )}
            </div>

            {convertRow && (
                <ConvertDialog
                    row={convertRow}
                    onClose={() => setConvertRow(null)}
                    onDone={() => { setConvertRow(null); load(); }}
                />
            )}
        </div>
    );
}

function WebhookConfigCard({ webhook }) {
    if (!webhook) return null;
    const copy = async (v) => {
        if (!v) return;
        try { await navigator.clipboard.writeText(v); toast.success("Copied"); }
        catch { toast.error("Copy failed"); }
    };
    return (
        <section className="border border-ikb/20 bg-ikb/[0.03] p-4 sm:p-5 mb-5" data-testid="inbox-webhook-config">
            <div className="text-[10px] uppercase tracking-widest font-mono text-ikb">Setup · one-time</div>
            <h2 className="font-display font-bold text-base sm:text-lg mt-0.5">Point Twilio & Resend at these URLs</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                <WebhookRow
                    label="Twilio inbound (SMS + WhatsApp)"
                    url={webhook.twilio_inbound_url}
                    signed={webhook.twilio_signed}
                    hint={webhook.hint?.twilio}
                    onCopy={() => copy(webhook.twilio_inbound_url)}
                />
                <WebhookRow
                    label="Resend inbound (email)"
                    url={webhook.resend_inbound_url}
                    signed={webhook.resend_signed}
                    hint={webhook.hint?.resend}
                    onCopy={() => copy(webhook.resend_inbound_url)}
                />
            </div>
            {(!webhook.twilio_inbound_url || !webhook.resend_inbound_url) && (
                <p className="text-[11px] font-mono text-amber-800 bg-amber-50 border border-amber-200 px-2 py-1.5 mt-3">
                    Missing company slug or CANONICAL_BASE_URL — ask an admin to set up the company slug on this workspace.
                </p>
            )}
        </section>
    );
}

function WebhookRow({ label, url, signed, hint, onCopy }) {
    return (
        <div className="border border-border bg-white p-3">
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">
                <span>{label}</span>
                <span className={`text-[9px] px-1.5 py-0.5 border ${signed ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-amber-300 bg-amber-50 text-amber-700"}`}>
                    {signed ? "Signed" : "Unsigned"}
                </span>
            </div>
            <div className="mt-1.5 flex items-center gap-1.5">
                <code className="flex-1 text-[11px] font-mono bg-secondary/50 px-2 py-1 truncate" title={url || ""} data-testid={`webhook-url-${label.split(" ")[0].toLowerCase()}`}>
                    {url || "— not configured —"}
                </code>
                <button type="button" onClick={onCopy} disabled={!url} className="p-1.5 border border-border hover:bg-secondary disabled:opacity-40" title="Copy">
                    <Copy className="w-3 h-3" />
                </button>
            </div>
            {hint && <p className="text-[11px] text-muted-foreground mt-2 leading-snug">{hint}</p>}
        </div>
    );
}

function InboxRow({ row, onConvert, onDismiss, onInvite }) {
    const meta = CHANNEL_META[row.channel] || CHANNEL_META.email;
    const Icon = meta.icon;
    const [inviting, setInviting] = useState(false);
    const inviteEmail = /^[\w.+-]+@[\w-]+\.[\w.-]+$/i.exec(row.from_addr || "")?.[0] || null;

    return (
        <li className="p-3 sm:p-4 hover:bg-secondary/40" data-testid={`inbox-row-${row.id}`}>
            <div className="flex items-start gap-3">
                <div className={`w-9 h-9 shrink-0 border flex items-center justify-center ${meta.tint}`}>
                    <Icon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 border ${meta.tint}`}>{meta.label}</span>
                        <span className="text-[11px] font-mono text-muted-foreground">{new Date(row.created_at).toLocaleString()}</span>
                        {row.status !== "open" && (
                            <span className="text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 border border-border">{row.status}</span>
                        )}
                    </div>
                    <div className="text-sm mt-1.5">
                        <span className="text-muted-foreground">From: </span>
                        <span className="font-medium">{row.from_addr || "—"}</span>
                        {row.to_addr && <span className="text-muted-foreground text-[11px] font-mono"> → {row.to_addr}</span>}
                    </div>
                    {row.subject && (
                        <div className="text-sm mt-0.5"><span className="text-muted-foreground">Subject: </span><span className="font-medium">{row.subject}</span></div>
                    )}
                    <blockquote className="mt-2 border-l-2 border-border pl-3 text-sm text-foreground/80 whitespace-pre-wrap line-clamp-3" data-testid={`inbox-row-body-${row.id}`}>
                        {row.body || "(empty body)"}
                    </blockquote>
                    {row.status === "open" && (
                        <div className="flex items-center gap-1.5 mt-3 flex-wrap">
                            <Button size="sm" onClick={onConvert} className="rounded-none h-8 bg-ikb hover:bg-[#001f7a] text-white" data-testid={`inbox-convert-${row.id}`}>
                                <UserPlus className="w-3.5 h-3.5 mr-1.5" /> Convert to candidate
                            </Button>
                            <Button
                                size="sm" variant="outline"
                                onClick={async () => { setInviting(true); try { await onInvite(inviteEmail); } finally { setInviting(false); } }}
                                disabled={inviting}
                                className="rounded-none h-8"
                                title={inviteEmail ? `Invite ${inviteEmail} to the candidate portal` : "Row has no email — convert first"}
                                data-testid={`inbox-invite-${row.id}`}
                            >
                                <Send className="w-3.5 h-3.5 mr-1.5" /> {inviting ? "Sending…" : "Send portal invite"}
                            </Button>
                            <Button size="sm" variant="outline" onClick={onDismiss} className="rounded-none h-8 border-rose-200 text-rose-700 hover:bg-rose-50" data-testid={`inbox-dismiss-${row.id}`}>
                                <X className="w-3.5 h-3.5 mr-1.5" /> Dismiss
                            </Button>
                        </div>
                    )}
                    {row.status === "converted" && row.candidate_id && (
                        <div className="mt-3">
                            <a
                                href={`/candidates/${row.candidate_id}`}
                                className="text-[11px] font-mono uppercase tracking-widest text-ikb inline-flex items-center gap-1"
                                data-testid={`inbox-open-cand-${row.id}`}
                            >
                                <ExternalLink className="w-3 h-3" /> Open candidate
                            </a>
                        </div>
                    )}
                </div>
            </div>
        </li>
    );
}

function ConvertDialog({ row, onClose, onDone }) {
    const inferredEmail = /^[\w.+-]+@[\w-]+\.[\w.-]+$/i.exec(row.from_addr || "")?.[0] || "";
    const inferredPhone = row.channel === "email" ? "" : (row.from_addr || "").replace(/^whatsapp:/, "");
    const [name, setName] = useState(row.subject || (inferredEmail ? inferredEmail.split("@")[0] : ""));
    const [email, setEmail] = useState(inferredEmail);
    const [phone, setPhone] = useState(inferredPhone);
    const [invite, setInvite] = useState(false);
    const [busy, setBusy] = useState(false);

    const submit = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(`/inbox/${row.id}/convert`, {
                name: name.trim() || undefined,
                email: email.trim() || undefined,
                phone: phone.trim() || undefined,
                send_portal_invite: invite,
            });
            toast.success(data?.created ? "Candidate created" : "Linked to existing candidate");
            if (data?.invite_url) {
                try { await navigator.clipboard.writeText(data.invite_url); toast.info("Portal invite link copied"); } catch { /* clipboard blocked */ }
            }
            onDone();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };

    return (
        <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="rounded-none max-w-md" data-testid="inbox-convert-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display font-bold text-xl">Convert to candidate</DialogTitle>
                </DialogHeader>
                <div className="space-y-2.5">
                    <label className="block">
                        <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Name</span>
                        <Input value={name} onChange={(e) => setName(e.target.value)} className="rounded-none mt-1" data-testid="convert-name" />
                    </label>
                    <label className="block">
                        <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Email</span>
                        <Input value={email} onChange={(e) => setEmail(e.target.value)} className="rounded-none mt-1" placeholder="candidate@email.com" data-testid="convert-email" />
                    </label>
                    <label className="block">
                        <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Phone</span>
                        <Input value={phone} onChange={(e) => setPhone(e.target.value)} className="rounded-none mt-1" placeholder="+1 415 555 2671" data-testid="convert-phone" />
                    </label>
                    <label className="flex items-center gap-2 text-sm cursor-pointer mt-2">
                        <input type="checkbox" checked={invite} onChange={(e) => setInvite(e.target.checked)} data-testid="convert-invite" />
                        <span>Send portal invite email <span className="text-muted-foreground">(requires an email address)</span></span>
                    </label>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} className="rounded-none">Cancel</Button>
                    <Button onClick={submit} disabled={busy || (!email.trim() && !phone.trim())} className="rounded-none bg-ikb hover:bg-[#001f7a] text-white" data-testid="convert-submit">
                        {busy ? "Converting…" : "Convert"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
